(ns ductile.ui.commands
  (:require ["cmdk" :refer [Command]]
            ["hotkeys-js" :as hotkeys]
            [applied-science.js-interop :as j]
            [clojure.string :as str]
            [ductile.scope-auth :as scope-auth]
            [ductile.time.format :as time-format]
            [ductile.ui.components :as ui]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.fahrzeug :as fahrzeug]
            [ductile.ui.http :as http]
            [ductile.ui.locale :as locale :refer [tr]]
            [ductile.ui.router :as router]
            [ductile.ui.scene :as scene]
            [ductile.ui.scope :as ui.scope]
            [ductile.ui.util :as util]
            [kitchen-async.promise :as p]
            [nextjournal.clerk.render.hooks :as hooks]
            [reagent.core :as r]))

(defn highlight-query [query text]
  (let [start (.indexOf (str/lower-case text) (str/lower-case (or query "")))
        end (+ start (count query))]
    (if (neg? start)
      text
      [:<>
       (subs text 0 start)
       [:span.font-bold.bg-yellow-500
        (subs text start end)]
       (subs text end)])))

(def !open? (deframe/!get ::open?))
(def !loading? (deframe/!get ::loading?))
(def !search (deframe/!get ::search ""))
(def !pages (deframe/!get ::pages [:home]))
(def !page (r/track #(last @!pages)))

(defn open! []
  (deframe/tx! [[:op/assoc ::open? true]]))

(defn close! []
  (deframe/tx! [[:op/assoc ::open? false]
                [:op/assoc ::pages [:home]]
                [:op/dissoc ::error]
                [:op/dissoc ::vin-search]
                [:op/dissoc ::license-plate-search]
                [:op/dissoc ::search]]))

(defn toggle-open! [^js e]
  (when e (.preventDefault e)
        (.stopPropagation e))
  (if @!open? (close!) (open!)))

(defn item [opts & content]
  [:> Command.Item
   (-> opts
       (dissoc :shortcut :close-on-select?)
       (assoc :on-select #(do ((:on-select opts))
                              (when (:close-on-select? opts)
                                (close!)))))
   (into [:div.w-full] content)
   (when (:shortcut opts)
     (into [:div.command-shortcut]
           (map (fn [key] [:kbd {:key key} key]))
           (map ui/platform-key (:shortcut opts))))])

(defn vin-result-item [i fahrzeug]
  [item
   {:on-select #(router/navigate-to (router/path '[compound :compound-name vehicle :fahrzeug-id]
                                                 {:compound-name (:compound/name fahrzeug)
                                                  :fahrzeug-id (:ductile/id fahrzeug)}))
    :data-testid (str "vin-" (:vehicle/vin fahrzeug) "-" i)
    :close-on-select? true}

   [:div.flex.gap-3.items-center.justify-between {:class (str "py-1.5"
                                                              (when (or (not (:entry/datetime fahrzeug))
                                                                        (:exit/datetime fahrzeug)) " opacity-70"))}
    [:div.flex.gap-3.items-center
     [:div.w-9.h-9.flex.items-center.justify-center.bg-white.rounded {:class "p-[2px]"}
      [ui/make-logo (:make/name fahrzeug)]]
     [:div.flex.justify-between.w-100
      [:div (:make/name fahrzeug) " "
       (when-let [model (:vehicle/model fahrzeug)]
         [:span.font-bold model])
       (when-let [lp (:vehicle/license-plate fahrzeug)]
         [:div.font-mono.text-sm lp])
       [:div [ui/vin (:vehicle/vin fahrzeug)]]
       (when (:contract/name fahrzeug) [:div (tr :contract/name) ": " (:contract/name fahrzeug)])
       [:div [ui/badge {:variant :gray :size 1} (:compound/name fahrzeug)]]]]]
    [:div.tabular-nums.text-right
     (when (:entry/datetime fahrzeug) [:div (tr "Entry") ": " (time-format/->str (:entry/datetime fahrzeug))])
     (when (:exit/datetime fahrzeug) [:div (tr "Exit") ": " (time-format/->str (:exit/datetime fahrzeug))])
     (when-let [{:parking-spot/keys [zone row place]} (:fahrzeug/parking-spot fahrzeug)]
       [:div
        [:span (tr "Parking spot") ": "]
        [:span.tabular-nums (str/join "/" (filter some? [zone row place]))]])]]])

(defn open-page-ops [page]
  [[:op/assoc ::pages (vec (conj @!pages page))]
   [:op/dissoc ::search]])

(def bounce-duration 100)

(defn try-select-first-item! []
  (when-not (js/document.querySelector "[cmdk-item][data-selected='true']")
    (let [event (js/document.createEvent "HTMLEvents")]
      (.initEvent event "keydown" true true)
      (j/assoc! event :key "Home")
      (.dispatchEvent (js/document.querySelector "[cmdk-input]") event))))

(defn open-page! [page]
  (when-not @!open? (open!))
  (deframe/tx! (open-page-ops page))
  (js/setTimeout try-select-first-item! bounce-duration))

(defn pop-page! []
  (deframe/assoc! ::pages (vec (butlast @!pages)))
  (js/setTimeout try-select-first-item! bounce-duration))

(def page-names
  {:home (tr "Home")
   :vin-search (tr "Search VINs")
   :license-plate-search (tr "Search License Plates")
   :switch-compound (tr "Compounds")
   :switch-locale (tr "Languages")})

(defn bounce! [{:keys [!ref]}]
  (when-let [el @!ref]
    (j/assoc-in! el [:style :transform] "scale(0.96)")
    (js/setTimeout #(when-let [el @!ref]
                      (j/assoc-in! el [:style :transform] "")) bounce-duration)))

(defn on-value-change [v]
  (let [vin-search? (#{:vin-search} @!page)
        license-plate-search? (#{:license-plate-search} @!page)
        filtered-v (if vin-search?
                     (apply str (re-seq #"[A-HJ-NPR-Z0-9]" (str/upper-case v)))
                     v)]
    (deframe/assoc! ::search filtered-v)
    (cond
      vin-search?
      (if (< 5 (count filtered-v))
        (do
          (deframe/tx! [[:op/dissoc-in [::vin-search :results]]
                        [:op/dissoc ::error]
                        [:op/assoc ::loading? true]])
          (.then (http/$vehicle-search {:ductile.vehicle-search/use-case :vin-search
                                        :search-params (cond-> {:vehicle/vin (if (util/full-vin? filtered-v)
                                                                               filtered-v
                                                                               {:ductile.vehicle-search/regex (str "(?i)" filtered-v)})
                                                                :compound/name (set (:user/compounds @deframe/!current-user))})
                                        :limit 10})
                 (fn [results]
                   (if (:error results)
                     (deframe/tx! [[:op/assoc ::error results]
                                   [:op/dissoc ::loading?]])
                     (deframe/tx! [[:op/assoc-in [::vin-search :results] (reverse (sort-by :entry/datetime results))]
                                   [:op/dissoc ::loading?]])))))
        (deframe/tx! [[:op/dissoc ::error]
                      [:op/dissoc-in [::vin-search :results]]]))

      license-plate-search?
      (if (< 4 (count filtered-v))
        (do
          (deframe/tx! [[:op/dissoc-in [::license-plate-search :results]]
                        [:op/dissoc ::error]
                        [:op/assoc ::loading? true]])
          (.then (http/$vehicle-search {:ductile.vehicle-search/use-case :license-plate-search
                                        :search-params (cond-> {:vehicle/license-plate {:ductile.vehicle-search/regex (str "(?i)" filtered-v)}
                                                                :compound/name (set (:user/compounds @deframe/!current-user))})
                                        :limit 10})
                 (fn [results]
                   (if (:error results)
                     (deframe/tx! [[:op/assoc ::error results]
                                   [:op/dissoc ::loading?]])
                     (deframe/tx! [[:op/assoc-in [::license-plate-search :results] (reverse (sort-by :entry/datetime results))]
                                   [:op/dissoc ::loading?]])))))
        (deframe/tx! [[:op/dissoc ::error]
                      [:op/dissoc-in [::license-plate-search :results]]])))))

(defn on-key-down [opts event]
  (cond (= (.-key event) "Escape")
        (do (.preventDefault event)
            (if (= @!page :home)
              (close!)
              (pop-page!)))
        (= (.-key event) "Enter")
        (bounce! opts)
        (and (= (.-key event) "Backspace") (str/blank? @!search) (not= :home @!page))
        (do (.preventDefault event)
            (pop-page!)
            (bounce! opts))
        (and (= (.-key event) "k") (.-metaKey event))
        (do (.preventDefault event)
            (close!))))

(defn command-list [commands]
  (into [:<>]
        (keep (fn [[heading cmds]]
                (let [cmds (keep (fn [cmd]
                                   (if-let [required-scope (:scope cmd)]
                                     (when (scope-auth/authorized-for? (:user/write-scopes @deframe/!current-user) required-scope)
                                       [item (dissoc cmd :label :group :page) (:label cmd)])
                                     ;;FIXME: the scope viz wrapper div breaks the Command.List
                                     #_(ui.scope/when-current-user-scope
                                        required-scope
                                        [item (dissoc cmd :label :group :page) (:label cmd)])
                                     [item (dissoc cmd :label :group :page) (:label cmd)]))
                                 cmds)]
                  (when (not-empty cmds)
                    (into (if heading [:> Command.Group {:heading heading}] [:<>])
                          cmds)))))
        (->> commands
             (group-by :group))))

(defn ->binding [ks]
  (->> ks (map ui/platform-key) (str/join "+")))

(def toggle-binding
  (->binding [:cmd "k"]))

(defn menu []
  (let [path-params {:compound-name @deframe/!current-compound
                     :compound/name @deframe/!current-compound
                     :user @deframe/!current-user}
        nav (router/build-nav path-params)
        !ref (hooks/use-ref nil)
        home (remove nil? (concat
                           [{:label (str (tr "Search for VIN") "…")
                             :shortcut [:cmd "o"]
                             :on-select #(open-page! :vin-search)
                             :scope :scope/workflow}
                            {:label (str (tr "Search License Plates") "…")
                             :on-select #(open-page! :license-plate-search)
                             :scope :scope/workflow}
                            {:label (str (tr "Switch compound") "…")
                             :on-select #(open-page! :switch-compound)}
                            {:label (str (tr "Switch language") "…")
                             :on-select #(open-page! :switch-locale)}
                            {:label "Select Defscene…"
                             :on-select #(open-page! :select-defscene)
                             :scope :scope/developer
                             :group (tr "Developer")}
                            {:label (tr "Toggle debug mode")
                             :shortcut [:option :cmd "d"]
                             :on-select ui/toggle-debug-mode!
                             :scope :scope/developer
                             :group (tr "Developer")
                             :close-on-select? true}
                            {:label (tr "Toggle scopes debug mode")
                             :on-select ui.scope/toggle-scopes-debug-mode!
                             :scope :scope/developer
                             :group (tr "Developer")
                             :close-on-select? true}
                            {:label (tr "Start a new speculative session")
                             :on-select #(http/$enable-speculative-transactions!
                                          {:ductile.datomic.speculative-transactions/scope :session})
                             :scope :scope/developer
                             :group (tr "Developer")
                             :close-on-select? true}
                            {:label (tr "Startpage")
                             :on-select #(router/navigate-to "/")
                             :close-on-select? true}
                            {:label (tr "Clear localstorage")
                             :on-select #(do (.clear js/localStorage)
                                             (router/navigate-to (router/current-path)))
                             :close-on-select? true}
                            {:label (tr "Toggle new vehicle details page")
                             :on-select fahrzeug/toggle-new-fahrzeug-page!
                             :close-on-select? true}]
                           (mapcat
                            (fn [item]
                              (let [{:keys [label items template mobile?]} (scope-auth/unwrap-protected-value item)]
                                (if items
                                  (map (fn [item]
                                         (let [{:as item :keys [template mobile?]} (scope-auth/unwrap-protected-value item)
                                               path (router/path template path-params)]
                                           {:label (:label item)
                                            :on-select #(if mobile?
                                                          (ui/open-popup! ui/mobile-preview-popup-opts path)
                                                          (router/navigate-to path))
                                            :close-on-select? true
                                            :group label}))
                                       items)
                                  [{:label label
                                    :on-select #(router/navigate-to (router/path template path-params))
                                    :close-on-select? true}])))
                            (router/build-nav {:compound-name @deframe/!current-compound
                                               :user @deframe/!current-user}))))]
    (hooks/use-effect
     (fn []
       (hotkeys toggle-binding toggle-open!)
       (doseq [cmd home]
         (when (:shortcut cmd)
           (hotkeys (->binding (:shortcut cmd))
                    #(do (when (or (not (:scope cmd))
                                   (util/has-required-scope? (:scope cmd)))
                           ((:on-select cmd)))
                         false))))
       (fn []
         (.unbind hotkeys toggle-binding)
         (doseq [cmd home]
           (when (:shortcut cmd)
             (.unbind hotkeys (->binding (:shortcut cmd))))))))
    [:div.command-menu
     [:> Command.Dialog {:key (str @!page) ;; clear uncontrolled input when page changes
                         :open @(deframe/!get ::open?)
                         :on-open-change #(when-not % (close!))
                         :on-key-down (partial on-key-down {:!ref !ref})
                         :should-filter (not (#{:vin-search :license-plate-search} @!page))
                         :data-testid "command-dialog"
                         :ref !ref}
      (into [:div]
            (map (fn [page]
                   [:div.command-badge.uppercase
                    (page-names page (str/replace (name (:defscene-ns page page))
                                                  #"-" " "))]))
            @!pages)
      [:> Command.Input {:placeholder (cond
                                        (= :vin-search @!page)
                                        (tr "Search for a VIN")
                                        (= :license-plate-search @!page)
                                        (tr "Search License Plates")
                                        :else
                                        (tr "What do you need?"))
                         :on-value-change on-value-change ;; keep uncontrolled, otherwise loses characters when typing fast
                         :on-key-down (fn [^js e]
                                        ;; cmdk intercepts home/end for list navigation
                                        (when (#{"Home" "End"} (.-key e))
                                          (.stopPropagation e)
                                          (let [input (.-target e)
                                                len (.-length (.-value input))]
                                            (case (.-key e)
                                              "Home" (.setSelectionRange input 0 0)
                                              "End" (.setSelectionRange input len len)))))}]
      [:> Command.List
       (if @!loading?
         [:> Command.Loading (cond
                               (= :vin-search @!page) (str (tr "Searching for VINs") "…")
                               (= :license-plate-search @!page) (str (tr "Searching for License Plates") "…")
                               :else (tr "Loading…"))]
         [:> Command.Empty (tr "Nothing found")])
       (cond
         @(deframe/!get ::error)
         [ui/error-view @(deframe/!get ::error)]

         (= :vin-search @!page)
         (into [:<>]
               (map-indexed (fn [i fahrzeug] [vin-result-item i fahrzeug]))
               @(deframe/!get-in [::vin-search :results]))

         (= :license-plate-search @!page)
         (into [:<>]
               (map-indexed (fn [i fahrzeug] [vin-result-item i fahrzeug]))
               @(deframe/!get-in [::license-plate-search :results]))

         (= :switch-compound @!page)
         (into [:<>]
               (map
                (fn [compound-name]
                  [item
                   {:on-select #(p/let [{:keys [selected-compound]} (http/$lambda 'ductile.lambda/handle-select-compound
                                                                                  {:compound/name compound-name})]
                                  (deframe/select-compound! selected-compound)
                                  (router/navigate-to (router/same-route-different-compound selected-compound)))
                    :close-on-select? true}
                   compound-name]))
               (:user/compounds @deframe/!current-user))
         (= :switch-locale @!page)
         (into [:<>]
               (map (fn [lang]
                      [item
                       {:on-select #(locale/set-locale! lang)
                        :close-on-select? true}
                       (locale/language-label lang)]))
               locale/supported-languages)
         (= :select-defscene @!page)
         (into [:<>]
               (map (fn [[ns scenes]]
                      [item
                       {:on-select #(open-page! {:defscene-ns ns})}
                       (name ns)])
                    @scene/registry))
         (:defscene-ns @!page)
         (into [:<>
                [item {:on-select #(router/navigate-to (router/path '[developer scenes :ns]
                                                                    {:ns (name (:defscene-ns @!page))}))
                       :close-on-select? true}
                 (tr "All scenes")]]
               (map (fn [scene]
                      (let [title (name (:id scene))]
                        [item
                         {:on-select #(router/navigate-to (router/path '[developer scenes :ns :id]
                                                                       {:ns (name (:defscene-ns @!page))
                                                                        :id title}))
                          :close-on-select? true}
                         title]))
                    (get @scene/registry (:defscene-ns @!page))))
         :else
         (command-list home))]]]))
